import { ButtonProps, InputProps } from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { IQuoteRespSchema } from '@monetize/types/app';
import { Maybe } from '@monetize/types/common';
import { KeyboardEvent, useEffect, useMemo, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';
import { handleApiErrorToast } from '../../api/axios';
import { getQuoteStatus } from '../../utils';
import { MBox, MButton, MInput, MText } from '../Monetize';
import { BlankTextOrContent } from '../Monetize/BlankTextOrContent';

const DEFAULT_MAX_WIDTH = '160px';

// Pinning the horizontal footprint and wrapping long values across multiple
// lines keeps the row layout stable in the Details panel — see BP-17565.
const wrapStyles = {
  whiteSpace: 'normal',
  wordBreak: 'break-word',
} as const;

interface QuoteInlineEditableTextFieldProps {
  /**
   * Gates editing on the quote's status. Omit when the field is not backed by a
   * saved quote (e.g. the one-time invoice create form), in which case only
   * `isReadOnly` decides whether the field can be edited.
   */
  quote?: IQuoteRespSchema;
  value: Maybe<string>;
  placeholder: string;
  maxLength?: number;
  onSave: (value: Maybe<string>) => Promise<void>;
  setIsQuoteSaving?: (val: boolean) => void;
  isReadOnly?: boolean;
  maxWidth?: string | number;
  buttonProps?: ButtonProps;
  inputProps?: InputProps;
  previewPropsNonEdit?: ButtonProps;
}

type FormValues = { value?: Maybe<string> };

export const QuoteInlineEditableTextField = ({
  quote,
  value,
  placeholder,
  maxLength,
  onSave,
  setIsQuoteSaving,
  isReadOnly = false,
  maxWidth = DEFAULT_MAX_WIDTH,
  buttonProps,
  inputProps,
  previewPropsNonEdit,
}: QuoteInlineEditableTextFieldProps) => {
  const quoteStatus = quote ? getQuoteStatus(quote) : null;
  const canEdit =
    !isReadOnly &&
    (!quoteStatus ||
      quoteStatus.isReview ||
      quoteStatus.isApproved ||
      quoteStatus.isDraft ||
      quoteStatus.isAccepted ||
      quoteStatus.isAcceptedAdminEditable);

  const [loading, setLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  // Prevents the blur-triggered save from running when Escape just cancelled.
  const justCancelledRef = useRef(false);

  const schema = useMemo(() => {
    const stringSchema = maxLength
      ? z.string().max(maxLength, `Maximum ${maxLength} characters allowed`)
      : z.string();
    return z.object({ value: stringSchema.nullish() });
  }, [maxLength]);

  const {
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    mode: 'onChange',
    defaultValues: { value: value ?? '' },
  });

  useEffect(() => {
    reset({ value: value ?? '' });
  }, [value, reset]);

  const onSubmit = async ({ value: submittedValue }: FormValues) => {
    const normalized = submittedValue?.trim() || null;
    const current = value?.trim() || null;
    if (normalized === current) {
      reset({ value: value ?? '' });
      setIsEditing(false);
      return;
    }
    setLoading(true);
    setIsQuoteSaving?.(true);
    try {
      await onSave(normalized);
    } catch (err) {
      handleApiErrorToast(err);
    } finally {
      setLoading(false);
      setIsEditing(false);
      setIsQuoteSaving?.(false);
    }
  };

  const handleCancel = () => {
    justCancelledRef.current = true;
    reset({ value: value ?? '' });
    setIsEditing(false);
  };

  const handleBlur = () => {
    if (justCancelledRef.current) {
      justCancelledRef.current = false;
      return;
    }
    void handleSubmit(onSubmit)();
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      handleCancel();
    } else if (event.key === 'Enter') {
      event.preventDefault();
      inputRef.current?.blur();
    }
  };

  if (!canEdit) {
    return (
      <MBox maxW={maxWidth} textAlign="right">
        <BlankTextOrContent value={value}>
          <MText color="tGray.darkPurple" {...wrapStyles}>
            {value}
          </MText>
        </BlankTextOrContent>
      </MBox>
    );
  }

  if (!isEditing && !value) {
    return (
      <MBox>
        <MButton
          variant="tertiary"
          onClick={() => setIsEditing(true)}
          size="sm"
          {...buttonProps}
        >
          + Add
        </MButton>
      </MBox>
    );
  }

  return (
    <Controller
      control={control}
      name="value"
      render={({ field: { onChange, value: fieldValue } }) =>
        isEditing ? (
          <MInput
            ref={inputRef}
            autoFocus
            value={fieldValue || ''}
            placeholder={placeholder}
            onChange={(event) => onChange(event.target.value)}
            onFocus={(event) => event.target.select()}
            onKeyDown={handleKeyDown}
            onBlur={handleBlur}
            isDisabled={!canEdit || loading}
            maxLength={maxLength}
            fontSize="sm"
            minH="2rem"
            w="auto"
            maxW={maxWidth}
            borderColor={errors?.value ? 'tRed.base' : 'tGray.lightPurple'}
            color="tPurple.base"
            {...inputProps}
          />
        ) : (
          <MButton
            variant="tertiary"
            size="sm"
            onClick={() => setIsEditing(true)}
            fontWeight="normal"
            minH="2rem"
            height="auto"
            maxW={maxWidth}
            justifyContent="flex-end"
            textAlign="right"
            py={1}
            {...wrapStyles}
            {...previewPropsNonEdit}
          >
            {value}
          </MButton>
        )
      }
    />
  );
};
